import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Key,
  Plus,
  Trash2,
  Copy,
  RefreshCw,
  Eye,
  EyeOff,
  Calendar,
  AlertTriangle,
  ScrollText,
} from 'lucide-react';
import { useToast } from '@/components/ui/ToastProvider';
import { getAuthToken } from '@/services/tokenStorage';

// Types
interface ApiToken {
  id: string;
  name: string;
  token_prefix: string;
  scopes: string[];
  expires_at?: string;
  last_used_at?: string;
  created_at: string;
  age_days?: number;
  needs_rotation?: boolean;
}

interface CreateTokenRequest {
  name: string;
  scopes: string[];
  expires_at?: string; // ISO 8601 timestamp
}

interface CreateTokenResponse {
  token: string; // Full token (only shown once)
  token_id: string;
  token_prefix: string;
  name: string;
  scopes: string[];
  expires_at?: string;
  created_at: string;
}

interface RotateTokenResponse {
  success: boolean;
  new_token: string;
  new_token_id: string;
  new_token_prefix: string;
  old_token_deleted: boolean;
  message: string;
}

interface TokenScopeInfo {
  value: string;
  label: string;
  description: string;
}

interface TokenRotationStatus {
  token_id: string;
  name: string;
  token_prefix: string;
  age_days: number;
  needs_rotation: boolean;
  last_used_at?: string;
  created_at: string;
}

interface TokenRotationStatusResponse {
  tokens_needing_rotation: TokenRotationStatus[];
  rotation_threshold_days: number;
}

// API base URL
const API_BASE = '/api/v1';

async function fetchTokens(): Promise<ApiToken[]> {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE}/tokens`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch tokens');
  }
  return response.json();
}

async function createToken(request: CreateTokenRequest): Promise<CreateTokenResponse> {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE}/tokens`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create token');
  }
  return response.json();
}

async function deleteToken(tokenId: string): Promise<void> {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE}/tokens/${tokenId}`, {
    method: 'DELETE',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error('Failed to delete token');
  }
}

async function fetchRotationStatus(): Promise<TokenRotationStatusResponse> {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE}/tokens/rotation-status`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch rotation status');
  }
  return response.json();
}

async function fetchScopes(): Promise<TokenScopeInfo[]> {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE}/tokens/scopes`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch scopes');
  }
  return response.json();
}

async function rotateToken(tokenId: string, newName?: string, deleteOld?: boolean): Promise<RotateTokenResponse> {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE}/tokens/${tokenId}/rotate`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ new_name: newName || undefined, delete_old: deleteOld }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to rotate token');
  }
  return response.json();
}

// Fallback if the scopes endpoint is unavailable (e.g. older backend).
const FALLBACK_SCOPES: TokenScopeInfo[] = [
  { value: 'read:packages', label: 'Read Packages', description: 'Read and search packages' },
  { value: 'publish:packages', label: 'Publish Packages', description: 'Publish new package versions' },
  { value: 'deploy:mocks', label: 'Deploy Mocks', description: 'Deploy hosted mock services' },
  { value: 'admin:org', label: 'Admin Organization', description: 'Full organization administration' },
  { value: 'read:usage', label: 'Read Usage', description: 'Read usage analytics and metrics' },
  { value: 'manage:billing', label: 'Manage Billing', description: 'Manage billing and subscription' },
];

export function ApiTokensPage() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [expiresDays, setExpiresDays] = useState<number | undefined>(undefined);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [rotateDialogTokenId, setRotateDialogTokenId] = useState<string | null>(null);
  const [rotateNewName, setRotateNewName] = useState('');
  const [rotateDeleteOld, setRotateDeleteOld] = useState(false);

  // Fetch tokens
  const { data: tokens, isLoading, error: tokensError, refetch: refetchTokens } = useQuery({
    queryKey: ['api-tokens'],
    queryFn: fetchTokens,
  });

  // Fetch the scope catalogue from the backend (falls back if unavailable).
  const { data: scopesFromApi } = useQuery({
    queryKey: ['api-token-scopes'],
    queryFn: fetchScopes,
    staleTime: 60 * 60 * 1000, // 1 hour
    retry: false,
  });
  const availableScopes: TokenScopeInfo[] =
    scopesFromApi && scopesFromApi.length > 0 ? scopesFromApi : FALLBACK_SCOPES;

  // Server-side rotation status (powers the summary banner).
  const { data: rotationStatus } = useQuery({
    queryKey: ['api-token-rotation-status'],
    queryFn: fetchRotationStatus,
    retry: false,
  });

  // Create token mutation
  const createTokenMutation = useMutation({
    mutationFn: createToken,
    onSuccess: (data) => {
      setNewToken(data.token);
      queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
      queryClient.invalidateQueries({ queryKey: ['api-token-rotation-status'] });
      setIsCreateDialogOpen(false);
      // Reset form
      setNewTokenName('');
      setSelectedScopes([]);
      setExpiresDays(undefined);
    },
    onError: (error: Error) => {
      showToast('error', 'Error', error.message || 'Failed to create token');
    },
  });

  // Delete token mutation
  const deleteTokenMutation = useMutation({
    mutationFn: deleteToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
      queryClient.invalidateQueries({ queryKey: ['api-token-rotation-status'] });
      showToast('success', 'Success', 'Token deleted successfully');
    },
    onError: (error: Error) => {
      showToast('error', 'Error', error.message || 'Failed to delete token');
    },
  });

  // Rotate token mutation
  const rotateTokenMutation = useMutation({
    mutationFn: ({ tokenId, newName, deleteOld }: { tokenId: string; newName?: string; deleteOld?: boolean }) =>
      rotateToken(tokenId, newName, deleteOld),
    onSuccess: (data) => {
      setNewToken(data.new_token);
      setRotateDialogTokenId(null);
      setRotateNewName('');
      setRotateDeleteOld(false);
      queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
      queryClient.invalidateQueries({ queryKey: ['api-token-rotation-status'] });
      showToast('success', 'Success', data.message);
    },
    onError: (error: Error) => {
      showToast('error', 'Error', error.message || 'Failed to rotate token');
    },
  });

  const handleRotateToken = () => {
    if (!rotateDialogTokenId) return;
    rotateTokenMutation.mutate({
      tokenId: rotateDialogTokenId,
      newName: rotateNewName.trim() || undefined,
      deleteOld: rotateDeleteOld,
    });
  };

  const handleCreateToken = () => {
    if (!newTokenName.trim()) {
      showToast('error', 'Error', 'Token name is required');
      return;
    }
    if (selectedScopes.length === 0) {
      showToast('error', 'Error', 'At least one scope is required');
      return;
    }
    const expiresAt =
      expiresDays && expiresDays > 0
        ? new Date(Date.now() + expiresDays * 24 * 60 * 60 * 1000).toISOString()
        : undefined;
    createTokenMutation.mutate({
      name: newTokenName,
      scopes: selectedScopes,
      expires_at: expiresAt,
    });
  };

  const handleCopyToken = (token: string) => {
    navigator.clipboard.writeText(token);
    showToast('success', 'Copied', 'Token copied to clipboard');
  };

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const isExpired = (expiresAt?: string) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">API Tokens</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Manage personal access tokens for CLI and API access
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <RouterLink
              to="/organization?tab=audit&event_type=api_token_created,api_token_deleted,api_token_rotated"
            >
              <ScrollText className="w-4 h-4 mr-2" />
              View Audit Log
            </RouterLink>
          </Button>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Token
          </Button>
        </div>
      </div>

      {/* Rotation Reminder Banner */}
      {rotationStatus && rotationStatus.tokens_needing_rotation.length > 0 && (
        <div
          className="rounded-lg border border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 p-4"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 mt-0.5 text-warning-600 dark:text-warning-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-warning-900 dark:text-warning-100">
                {rotationStatus.tokens_needing_rotation.length === 1
                  ? '1 token is older than '
                  : `${rotationStatus.tokens_needing_rotation.length} tokens are older than `}
                {rotationStatus.rotation_threshold_days} days and should be rotated.
              </p>
              <p className="text-sm text-warning-700 dark:text-warning-200 mt-1">
                Rotating long-lived tokens reduces the blast radius of leaked credentials.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* New Token Display Dialog */}
      {newToken && (
        <Dialog open={!!newToken} onOpenChange={() => setNewToken(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Token Created</DialogTitle>
              <DialogDescription>
                Copy this token now. You won't be able to see it again!
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="relative">
                <Input
                  type={showToken ? 'text' : 'password'}
                  value={newToken}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  onClick={() => setShowToken(!showToken)}
                >
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-3">
                <div className="flex items-start">
                  <AlertTriangle className="w-4 h-4 mr-2 text-warning-600 dark:text-warning-400 mt-0.5" />
                  <p className="text-sm text-warning-700 dark:text-warning-200">
                    Make sure to copy this token. It will not be shown again.
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setNewToken(null);
                  setShowToken(false);
                }}
              >
                Close
              </Button>
              <Button onClick={() => handleCopyToken(newToken)}>
                <Copy className="w-4 h-4 mr-2" />
                Copy Token
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Token Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create API Token</DialogTitle>
            <DialogDescription>
              Create a personal access token for CLI and API access
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="token-name">Token Name</Label>
              <Input
                id="token-name"
                placeholder="e.g., CLI Development"
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
              />
            </div>
            <div>
              <Label>Scopes</Label>
              <div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
                {availableScopes.map((scope) => (
                  <div
                    key={scope.value}
                    className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent cursor-pointer"
                    onClick={() => toggleScope(scope.value)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedScopes.includes(scope.value)}
                      onChange={() => toggleScope(scope.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{scope.label}</div>
                      <div className="text-sm text-muted-foreground">{scope.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="expires-days">Expires In (Days)</Label>
              <Input
                id="expires-days"
                type="number"
                placeholder="Leave empty for no expiration"
                value={expiresDays || ''}
                onChange={(e) =>
                  setExpiresDays(e.target.value ? parseInt(e.target.value) : undefined)
                }
                min="1"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Optional: Set expiration in days. Leave empty for no expiration.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateToken}
              disabled={createTokenMutation.isPending || !newTokenName.trim() || selectedScopes.length === 0}
            >
              {createTokenMutation.isPending ? 'Creating...' : 'Create Token'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rotate Token Dialog */}
      <Dialog open={!!rotateDialogTokenId} onOpenChange={() => setRotateDialogTokenId(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rotate Token</DialogTitle>
            <DialogDescription>
              Create a new token with the same scopes. The old token remains active unless you choose to delete it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="rotate-new-name">New Token Name (optional)</Label>
              <Input
                id="rotate-new-name"
                placeholder="Leave empty to keep current name"
                value={rotateNewName}
                onChange={(e) => setRotateNewName(e.target.value)}
              />
            </div>
            <div
              className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent cursor-pointer"
              onClick={() => setRotateDeleteOld(!rotateDeleteOld)}
            >
              <input
                type="checkbox"
                checked={rotateDeleteOld}
                onChange={() => setRotateDeleteOld(!rotateDeleteOld)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium">Delete old token</div>
                <div className="text-sm text-muted-foreground">
                  Immediately revoke the old token after rotation
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRotateDialogTokenId(null)}>
              Cancel
            </Button>
            <Button onClick={handleRotateToken} disabled={rotateTokenMutation.isPending}>
              {rotateTokenMutation.isPending ? 'Rotating...' : 'Rotate Token'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tokens List */}
      {tokensError ? <div role="alert" className="py-6 text-danger-600">{tokensError.message} <Button onClick={() => refetchTokens()}>Retry</Button></div> : isLoading ? (
        <div className="text-center py-12">Loading tokens...</div>
      ) : tokens && tokens.length > 0 ? (
        <div className="space-y-4">
          {tokens.map((token) => (
            <Card key={token.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="font-semibold">{token.name}</h3>
                      {isExpired(token.expires_at) && (
                        <Badge variant="destructive">Expired</Badge>
                      )}
                      {token.expires_at && !isExpired(token.expires_at) && (
                        <Badge variant="secondary">
                          Expires {formatDate(token.expires_at)}
                        </Badge>
                      )}
                      {token.needs_rotation && (
                        <Badge variant="destructive">
                          Needs Rotation — {token.age_days} days old
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-3">
                      <div className="flex items-center">
                        <Key className="w-4 h-4 mr-1" />
                        <span className="font-mono">{token.token_prefix}...</span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        Created: {formatDate(token.created_at)}
                      </div>
                      {token.last_used_at && (
                        <div className="flex items-center">
                          Last used: {formatDate(token.last_used_at)}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {token.scopes.map((scope) => {
                        const scopeInfo = availableScopes.find((s) => s.value === scope);
                        return (
                          <Badge key={scope} variant="outline">
                            {scopeInfo?.label || scope}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRotateDialogTokenId(token.id)}
                      title="Rotate token"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this token?')) {
                          deleteTokenMutation.mutate(token.id);
                        }
                      }}
                      disabled={deleteTokenMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Key className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No API Tokens</h3>
            <p className="text-muted-foreground mb-4">
              Create your first API token to get started with CLI and API access
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Token
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
